// The app shell: sign-in, or the (currently empty) workspace.
//
// M1 scope. The canvas, grid calibration and the asset search all land on top of this; what exists here is the
// auth boundary and the honest empty state.
import { lazy, Suspense, useEffect, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import { isConfigured, missingConfig, supabase } from "./lib/supabase";
import { SetPassword, SignIn } from "./ui/auth";
import { RESET_PATH, WELCOME_PATH } from "./lib/auth-rules";

/**
 * ⚠ THE THREE SURFACES ARE SPLIT BECAUSE THE PLAYERS' ONE IS THE SLOWEST DEVICE IN THE ROOM.
 *
 * Imported eagerly, every one of these lands in a single bundle — so a share link on a tablet or a television
 * at the table downloaded the ENTIRE GM application (composite editor, asset search, the connectors, the UVTT
 * importer, the whole library UI) in order to render a read-only board. That is the one screen in this app
 * that must come up fast on hardware nobody chose and a connection nobody controls.
 *
 * Splitting costs one extra request on each route and saves the other two routes' code on all of them. The
 * shell above stays eager: auth and the share-token check must run without waiting for anything.
 */
const Workspace = lazy(() => import("./ui/workspace").then((m) => ({ default: m.Workspace })));
const TableView = lazy(() => import("./ui/table-view").then((m) => ({ default: m.TableView })));
const HandoutView = lazy(() => import("./ui/handout-view").then((m) => ({ default: m.HandoutView })));

/**
 * Share links are the ONE route that must work without an account.
 *
 * Checked before anything auth-related runs: a share viewer has no session and never will, and making them
 * wait on a session lookup that will always come back empty would put a spinner on a TV screen at the table.
 * Deliberately not a router — one path prefix does not justify the dependency.
 */
function shareToken(): { kind: "table" | "handout"; token: string } | null {
  const p = window.location.pathname;
  const t = /^\/t\/([A-Za-z0-9_-]{8,})\/?$/.exec(p);
  if (t) return { kind: "table", token: t[1]! };
  const h = /^\/h\/([A-Za-z0-9_-]{8,})\/?$/.exec(p);
  if (h) return { kind: "handout", token: h[1]! };
  return null;
}

/**
 * The two pages an emailed link opens.
 *
 * ⚠ CHECKED BEFORE THE SESSION IS, and that ordering is the bug waiting to happen. Both links arrive carrying
 * credentials, so by the time this renders the visitor HAS a session — and a session-first check would send
 * somebody who came to choose a password straight into the workspace instead, with the password unset and no
 * sign of what went wrong.
 */
function passwordRoute(): "welcome" | "reset" | null {
  const p = window.location.pathname.replace(/\/$/, "");
  if (p === WELCOME_PATH) return "welcome";
  if (p === RESET_PATH) return "reset";
  return null;
}

export function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [checking, setChecking] = useState(true);
  const token = shareToken();
  const setting = passwordRoute();

  useEffect(() => {
    if (!isConfigured || token || setting) {
      setChecking(false);
      return;
    }
    // Resolve the CURRENT session first, then subscribe. Subscribing alone is not enough: onAuthStateChange
    // does not replay what happened before it was attached, so a returning user with a valid session would sit
    // on the sign-in screen until something else woke the listener.
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setChecking(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setSession(s));
    return () => sub.subscription.unsubscribe();
  }, []);

  if (!isConfigured) return <NotConfigured missing={missingConfig()} />;
  // ⚠ The fallback says what is happening rather than flashing an empty page. On a television at the table
  // this is the only frame anyone sees before the board, so a bare white flash reads as a broken link.
  if (token) {
    return (
      <Suspense fallback={<Splash>Opening the board…</Splash>}>
        {token.kind === "handout"
          ? <HandoutView token={token.token} />
          : <TableView token={token.token} />}
      </Suspense>
    );
  }
  if (setting) return <SetPassword mode={setting} />;
  if (checking) return <Splash>Checking your session…</Splash>;
  if (!session) return <SignIn />;
  return (
    <Suspense fallback={<Splash>Loading your workspace…</Splash>}>
      <Workspace email={session.user.email ?? ""} userId={session.user.id} />
    </Suspense>
  );
}

function NotConfigured({ missing }: { missing: string[] }) {
  return (
    <main className="pane">
      <h1>Pip&rsquo;s VTT Maps isn&rsquo;t configured for this deployment.</h1>
      <p>
        {missing.map((m) => <code key={m}>{m}</code>).reduce<React.ReactNode[]>(
          (acc, el, i) => (i === 0 ? [el] : [...acc, " and ", el]), [],
        )}{" "}
        {missing.length > 1 ? "are" : "is"} not set here, so the app cannot reach its database.
      </p>
      <p className="muted">
        Netlify scopes environment variables — a Deploy Preview does not inherit a production-only value. Set
        them for this context and redeploy. No data has been touched.
      </p>
    </main>
  );
}

function Splash({ children }: { children: React.ReactNode }) {
  return <main className="pane"><p className="muted">{children}</p></main>;
}
